import { FieldShell, SectionCard } from '@atlas/ui-components'
import { Badge, Button, Combobox } from '@atlas/ui-primitives'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import type { Message } from '@/domain/message.types'
import { MessageTranslationStatus } from '@/domain/message.types'
import { isNonTerminal as isNonTerminalStatus, isStuck as isStuckJob } from '@/hooks/useMessages'

import { buildRequestKey } from './request-key'

const STATUS_BADGE: Record<
	MessageTranslationStatus,
	{ variant: 'muted' | 'primary' | 'success' | 'danger'; label: string }
> = {
	[MessageTranslationStatus.PENDING]: { variant: 'muted', label: 'Pending' },
	[MessageTranslationStatus.TRANSLATING]: { variant: 'primary', label: 'Translating…' },
	[MessageTranslationStatus.READY]: { variant: 'success', label: 'Ready' },
	[MessageTranslationStatus.FAILED]: { variant: 'danger', label: 'Failed' },
}

/** One locale variant row: badge, link to its own page, and (for non-source rows) an AI-translate action. */
function VariantRow({
	variant,
	currentMessageId,
	onTranslate,
	onRequestRetranslate,
	isRequesting,
	translateError,
}: {
	variant: Message
	currentMessageId: string
	onTranslate: (locale: string) => void
	/** A re-translate replaces existing (possibly hand-edited) content — the section confirms it in a dialog before running. */
	onRequestRetranslate: (locale: string) => void
	isRequesting: boolean
	/** Set when THIS row's own last translation request failed — never a shared/global flag. */
	translateError?: string | null
}) {
	const statusBadge = variant.translationStatus ? STATUS_BADGE[variant.translationStatus] : null
	const isNonTerminal = isNonTerminalStatus(variant.translationStatus)
	const isStuck = isStuckJob(variant.translationStatus, variant.updatedAt)
	// `isRequesting` covers the moment right after clicking, before the mutation's
	// response has updated the cached status — without it the button read its old
	// label for one extra round trip while already disabled. It's checked FIRST,
	// unconditionally: a stuck row's Retry click must disable for its own
	// round trip too, not stay clickable just because the row reads as stuck.
	const isBusy = isRequesting || (isNonTerminal && !isStuck)
	const isRetranslate = !isBusy && !isStuck && variant.translationStatus === MessageTranslationStatus.READY
	// The action stays available on Ready/Failed rows (regenerate / retry) — only
	// its label changes, so it never reads like "apply" on something already done.
	const actionLabel = isBusy
		? 'In progress…'
		: isStuck
			? 'Retry'
			: isRetranslate
				? 'Re-translate'
				: variant.translationStatus === MessageTranslationStatus.FAILED
					? 'Retry'
					: 'Apply AI translation'

	const handlePress = () => {
		if (isRetranslate) {
			onRequestRetranslate(variant.languageIso)
			return
		}
		onTranslate(variant.languageIso)
	}

	return (
		<li className="flex items-center justify-between gap-3 rounded-ds-md border border-ds-border px-4 py-3">
			<div className="flex items-center gap-2">
				{variant.messageId === currentMessageId ? (
					<span className="font-medium text-ds-text">{variant.languageIso}</span>
				) : (
					<Link to={`/messages/${variant.messageId}`} className="font-medium text-ds-primary hover:underline">
						{variant.languageIso}
					</Link>
				)}
				{variant.isSource ? (
					<Badge variant="secondary" size="sm">
						Source
					</Badge>
				) : null}
				{/* Always mounted (not just once a status exists) so the FIRST status
				    ever announced for this row also reaches assistive tech — a live
				    region that only appears alongside the content it's meant to
				    announce misses that first announcement in most AT. */}
				<span aria-live="polite" className="contents">
					{statusBadge ? (
						<Badge variant={statusBadge.variant} size="sm">
							{statusBadge.label}
						</Badge>
					) : null}
				</span>
			</div>
			{!variant.isSource ? (
				<div className="flex items-center gap-2">
					{variant.translationStatus === MessageTranslationStatus.FAILED && variant.translationFailureReason ? (
						<span className="max-w-xs whitespace-normal break-words text-ds-sm text-ds-danger">
							{variant.translationFailureReason}
						</span>
					) : null}
					{translateError ? (
						<span role="alert" className="max-w-xs whitespace-normal break-words text-ds-sm text-ds-danger">
							{translateError}
						</span>
					) : null}
					{/* Persistently mounted so a label change (Apply → In progress… →
					    Ready/Retry) is announced even though the click that caused it
					    keeps focus on the button itself, where a label-only DOM change
					    is otherwise easy for AT to miss. */}
					<span aria-live="polite" className="contents">
						<Button variant="secondary" size="sm" className="rounded-ds-full" isDisabled={isBusy} onPress={handlePress}>
							{actionLabel}
						</Button>
					</span>
				</div>
			) : null}
		</li>
	)
}

/**
 * Presentational Translations panel for `MessageDetail.tsx` — lists a
 * message's translation-group locale variants, an add-locale control, and an
 * "Apply AI translation" action per locale. No data-fetching: the page
 * container owns `useTranslationGroup`/`useRequestTranslation` and passes
 * everything in as props.
 */
export function TranslationsSection({
	variants,
	currentMessageId,
	availableLocaleOptions,
	onAddLocale,
	isAddingLocale,
	addLocaleError,
	onTranslate,
	requestingLocales,
	translateErrors,
}: {
	variants: Message[]
	currentMessageId: string
	/** Locales not yet present in the group — the add-locale picker's choices. */
	availableLocaleOptions: { value: string; label: string }[]
	onAddLocale: (locale: string) => void
	isAddingLocale: boolean
	/** Set when the last add-locale attempt failed — never leave the user guessing why nothing happened. */
	addLocaleError?: string | null
	onTranslate: (locale: string) => void
	/** `buildRequestKey(messageId, locale)` keys currently in flight — keyed by BOTH message and locale so a stale request from a previously-viewed message can never be mistaken for one on the currently-viewed page. */
	requestingLocales: ReadonlySet<string>
	/** `buildRequestKey(messageId, locale)` → error message, for a request that failed to even be submitted (network/validation, not a translation-job failure) — per-request, not a single shared flag, so a failure is shown next to the SPECIFIC row it happened on and can't be misattributed under concurrent requests. */
	translateErrors: ReadonlyMap<string, string>
}) {
	const [newLocale, setNewLocale] = useState<string | null>(null)
	// Overwriting an already-translated row (never a blank "Apply" click) risks
	// discarding hand-edited content, with no undo — confirm in a dialog first.
	const [retranslating, setRetranslating] = useState<string | null>(null)
	const wasAddingLocale = useRef(false)

	// Only clear the picker once the add actually succeeds — clearing it
	// unconditionally on click meant a failed add (e.g. a race with someone
	// else adding the same locale) left the user with no selection to just
	// retry, forcing them to reselect it from scratch.
	useEffect(() => {
		if (wasAddingLocale.current && !isAddingLocale && !addLocaleError) setNewLocale(null)
		wasAddingLocale.current = isAddingLocale
	}, [isAddingLocale, addLocaleError])

	// The page route doesn't remount when navigating between sibling locales
	// (only the data changes) — without this, a half-picked "add locale" value
	// from one message's panel would carry over into a different message's.
	useEffect(() => {
		setNewLocale(null)
	}, [currentMessageId])

	const handleAdd = () => {
		if (!newLocale) return
		onAddLocale(newLocale)
	}

	return (
		<SectionCard title="Translations" className="mt-4 p-6">
			<ul className="flex flex-col gap-2">
				{variants.map((variant) => {
					const requestKey = buildRequestKey(currentMessageId, variant.languageIso)
					return (
						<VariantRow
							key={variant.messageId}
							variant={variant}
							currentMessageId={currentMessageId}
							onTranslate={onTranslate}
							onRequestRetranslate={setRetranslating}
							isRequesting={requestingLocales.has(requestKey)}
							translateError={translateErrors.get(requestKey)}
						/>
					)
				})}
			</ul>

			{availableLocaleOptions.length > 0 ? (
				<div className="mt-4">
					<div className="flex items-end gap-3">
						<FieldShell label="Add locale">
							<Combobox
								aria-label="Add locale"
								options={availableLocaleOptions}
								value={newLocale}
								onChange={(v) => setNewLocale(v ?? null)}
								placeholder="Select a locale…"
							/>
						</FieldShell>
						<Button
							variant="secondary"
							className="rounded-ds-full"
							isDisabled={!newLocale || isAddingLocale}
							onPress={handleAdd}
						>
							{isAddingLocale ? 'Adding…' : 'Add'}
						</Button>
					</div>
					{addLocaleError ? (
						<p role="alert" className="mt-2 text-ds-sm text-ds-danger">
							{addLocaleError}
						</p>
					) : null}
				</div>
			) : null}
			<ConfirmDialog
				open={retranslating !== null}
				title={`Re-translate ${retranslating ?? ''}?`}
				description="This will replace its current content once the new translation is ready."
				confirmLabel="Re-translate"
				confirmVariant="danger"
				onConfirm={() => {
					if (retranslating) onTranslate(retranslating)
					setRetranslating(null)
				}}
				onCancel={() => setRetranslating(null)}
			/>
		</SectionCard>
	)
}
